import type {
  AiClauseOption,
  SectionAiResult,
  SectionAiSelection,
} from "../types/gameRuleKbGenerate";

export const parseSectionAiResults = (
  raw: Record<string, unknown> | null | undefined,
): Record<string, SectionAiResult> => {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const out: Record<string, SectionAiResult> = {};
  for (const [sectionId, value] of Object.entries(raw)) {
    if (!value || typeof value !== "object") {
      continue;
    }
    const row = value as Record<string, unknown>;
    const status = typeof row.status === "string" ? row.status : "completed";
    const optionsRaw = Array.isArray(row.options) ? row.options : [];
    const options = optionsRaw
      .map((item, index): AiClauseOption | null => {
        if (!item || typeof item !== "object") {
          return null;
        }
        const opt = item as Record<string, unknown>;
        const content = typeof opt.content === "string" ? opt.content.trim() : "";
        if (!content) {
          return null;
        }
        const label =
          typeof opt.label === "string" ? opt.label : `Option ${String.fromCharCode(65 + index)}`;
        const confidenceRaw = opt.confidence;
        const confidence =
          typeof confidenceRaw === "number"
            ? Math.max(0, Math.min(100, Math.round(confidenceRaw)))
            : 90 - index * 5;
        const blocksRaw = opt.blocks;
        const blocks = Array.isArray(blocksRaw)
          ? (blocksRaw.filter(
              (b): b is Record<string, unknown> => Boolean(b) && typeof b === "object",
            ) as readonly Record<string, unknown>[])
          : null;
        return { label, content, confidence, blocks };
      })
      .filter((item): item is AiClauseOption => item !== null);
    out[sectionId] = {
      status,
      options,
      error: typeof row.error === "string" ? row.error : undefined,
    };
  }
  return out;
};

export const parseSectionAiSelections = (
  raw: Record<string, unknown> | null | undefined,
): Record<string, SectionAiSelection> => {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const out: Record<string, SectionAiSelection> = {};
  for (const [sectionId, value] of Object.entries(raw)) {
    if (!value || typeof value !== "object") {
      continue;
    }
    const row = value as Record<string, unknown>;
    const finalContent =
      typeof row.final_content === "string"
        ? row.final_content
        : typeof row.finalContent === "string"
          ? row.finalContent
          : "";
    if (!finalContent.trim()) {
      continue;
    }
    const selectedIndexRaw = row.selected_index ?? row.selectedIndex;
    const selectedIndex =
      typeof selectedIndexRaw === "number" ? selectedIndexRaw : Number(selectedIndexRaw) || 0;
    out[sectionId] = {
      selectedIndex,
      finalContent,
      wasEdited: Boolean(row.was_edited ?? row.wasEdited),
      finalStructure:
        row.final_structure && typeof row.final_structure === "object"
          ? (row.final_structure as Record<string, unknown>)
          : row.finalStructure && typeof row.finalStructure === "object"
            ? (row.finalStructure as Record<string, unknown>)
            : null,
    };
  }
  return out;
};

export const buildAiSelectionsApiPayload = (
  selections: Readonly<Record<string, SectionAiSelection>>,
): Record<
  string,
  {
    selected_index: number;
    final_content: string;
    was_edited: boolean;
    final_structure?: Record<string, unknown>;
  }
> => {
  const payload: Record<
    string,
    {
      selected_index: number;
      final_content: string;
      was_edited: boolean;
      final_structure?: Record<string, unknown>;
    }
  > = {};
  for (const [sectionId, selection] of Object.entries(selections)) {
    const entry: {
      selected_index: number;
      final_content: string;
      was_edited: boolean;
      final_structure?: Record<string, unknown>;
    } = {
      selected_index: selection.selectedIndex,
      final_content: selection.finalContent.trim(),
      was_edited: selection.wasEdited,
    };
    if (selection.finalStructure && typeof selection.finalStructure === "object") {
      entry.final_structure = selection.finalStructure;
    }
    payload[sectionId] = entry;
  }
  return payload;
};

export const allSectionsHaveAiSelection = (
  sectionIds: readonly string[],
  selections: Readonly<Record<string, SectionAiSelection>>,
): boolean =>
  sectionIds.length > 0 &&
  sectionIds.every((id) => Boolean(selections[id]?.finalContent.trim()));

export type SectionAiGenerationState = "pending" | "generating" | "ready" | "failed";

export const getSectionAiGenerationState = (
  sectionId: string,
  aiResults: Readonly<Record<string, SectionAiResult>>,
  aiProgress: { readonly currentSectionId: string | null } | null,
  aiSessionStatus: string | null,
): SectionAiGenerationState => {
  const result = aiResults[sectionId];
  if (result?.status === "failed") {
    return "failed";
  }
  if (result?.status === "completed" && result.options.length > 0) {
    return "ready";
  }
  if (aiProgress?.currentSectionId === sectionId) {
    return "generating";
  }
  if (aiSessionStatus === "ai_running") {
    return "pending";
  }
  if (result?.status === "completed") {
    return "failed";
  }
  return "pending";
};

export const isSectionAiBrowsable = (state: SectionAiGenerationState): boolean =>
  state === "ready" || state === "failed";

export const countAiGeneratedSections = (
  sectionIds: readonly string[],
  aiResults: Readonly<Record<string, SectionAiResult>>,
): number =>
  sectionIds.filter((id) => {
    const result = aiResults[id];
    return result?.status === "completed" && result.options.length > 0;
  }).length;

export const sectionNeedsAiGeneration = (
  sectionId: string,
  aiResults: Readonly<Record<string, SectionAiResult>>,
): boolean => {
  const result = aiResults[sectionId];
  return !(result?.status === "completed" && result.options.length > 0);
};

export const anySectionNeedsAiGeneration = (
  sectionIds: readonly string[],
  aiResults: Readonly<Record<string, SectionAiResult>>,
): boolean => sectionIds.some((id) => sectionNeedsAiGeneration(id, aiResults));

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
